<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { Check, Copy, Link2, MessageCircle, Share2, Sparkles } from "@lucide/vue";
import { api } from "@/lib/api";
import type { Track } from "@/types";

const tracks = ref<Track[]>([]);
const selectedId = ref("");
const copied = ref("");
const tasks = ref([
  { id: 1, label: "生成一个可追踪试听链接", done: false, channel: "基础" },
  { id: 2, label: "发布 15 秒副歌短视频", done: false, channel: "短视频" },
  { id: 3, label: "联系 5 位垂类歌单主理人", done: false, channel: "歌单" },
  { id: 4, label: "邀请首批听众收藏与评论", done: false, channel: "私域" },
  { id: 5, label: "72 小时后复盘完播与转化", done: false, channel: "复盘" },
]);

const selected = computed(() => tracks.value.find((track) => track.id === selectedId.value));
const smartLink = computed(() => selected.value ? `${location.origin}/listen/${selected.value.id}?utm_source=wavecraft&utm_campaign=launch` : "");
const caption = computed(() => selected.value ? `新歌《${selected.value.title}》刚刚完成。它写给那些在夜里还不肯放弃的人——戴上耳机，告诉我你听到第几秒被击中了。` : "");
const progress = computed(() => Math.round((tasks.value.filter((task) => task.done).length / tasks.value.length) * 100));

onMounted(async () => {
  tracks.value = (await api.tracks()).tracks;
  selectedId.value = tracks.value[0]?.id || "";
  const saved = localStorage.getItem("wavecraft-growth-tasks");
  if (saved) tasks.value = JSON.parse(saved);
});

function toggleTask(id: number) {
  const task = tasks.value.find((item) => item.id === id);
  if (task) task.done = !task.done;
  localStorage.setItem("wavecraft-growth-tasks", JSON.stringify(tasks.value));
}

async function copy(value: string, kind: string) {
  await navigator.clipboard.writeText(value);
  copied.value = kind;
  if (kind === "link") {
    const first = tasks.value[0];
    first.done = true;
    localStorage.setItem("wavecraft-growth-tasks", JSON.stringify(tasks.value));
  }
  setTimeout(() => (copied.value = ""), 1600);
}
</script>

<template>
  <div class="growth-layout">
    <section class="panel campaign-builder">
      <div class="section-heading border-bottom">
        <div><p class="eyebrow">LAUNCH KIT</p><h3>给每首歌一个可执行的首发周</h3></div>
        <span class="model-label"><span /> {{ progress }}% 完成</span>
      </div>
      <div class="form-section">
        <label class="field"><span>选择推广作品</span><select v-model="selectedId"><option v-for="track in tracks" :key="track.id" :value="track.id">{{ track.title }}</option></select></label>
        <div v-if="selected" class="link-builder">
          <div><Link2 :size="18" /><span><small>公开试听链接</small><strong>{{ smartLink }}</strong></span></div>
          <button class="button button-dark button-sm" @click="copy(smartLink, 'link')"><Check v-if="copied === 'link'" :size="15" /><Copy v-else :size="15" />{{ copied === "link" ? "已复制" : "复制" }}</button>
        </div>
        <div v-else class="inline-empty"><Sparkles :size="19" /><span><strong>先生成一首作品</strong><small>作品进入曲库后即可创建公开试听链接。</small></span></div>
      </div>

      <div class="campaign-copy">
        <div class="field-heading"><span>首发文案</span><button class="magic-link" :disabled="!caption" @click="copy(caption, 'caption')"><Copy :size="14" />{{ copied === "caption" ? "已复制" : "复制文案" }}</button></div>
        <p>{{ caption || "选择作品后自动生成一版首发文案。" }}</p>
        <div class="channel-chips"><span>#新歌发布</span><span>#独立音乐人</span><span>#AI音乐</span><span>#耳机推荐</span></div>
      </div>

      <div class="platform-plan">
        <div><span class="platform-icon coral"><MessageCircle :size="18" /></span><p><strong>短视频平台</strong><small>副歌 12–18 秒 + 一句故事钩子</small></p><b>D-3</b></div>
        <div><span class="platform-icon lime"><Share2 :size="18" /></span><p><strong>私域首听</strong><small>邀请 20 位真实听众反馈与收藏</small></p><b>D-1</b></div>
        <div><span class="platform-icon mint"><Sparkles :size="18" /></span><p><strong>歌单触达</strong><small>提供歌名、风格、场景和试听链接</small></p><b>D+1</b></div>
      </div>
    </section>

    <aside class="panel sprint-card">
      <div class="section-heading">
        <div><p class="eyebrow">7-DAY SPRINT</p><h3>首发任务</h3></div>
        <span class="score-badge">{{ tasks.filter((task) => task.done).length }}/{{ tasks.length }}</span>
      </div>
      <div class="task-list">
        <button v-for="task in tasks" :key="task.id" :class="{ done: task.done }" @click="toggleTask(task.id)">
          <span><Check :size="14" /></span>
          <p><strong>{{ task.label }}</strong><small>{{ task.channel }}</small></p>
        </button>
      </div>
      <div class="sprint-progress"><i :style="{ width: `${progress}%` }" /><span>{{ progress }}%</span></div>
      <p class="chart-note">任务只保存在当前设备；公开试听播放会进入数据看板。</p>
    </aside>
  </div>
</template>
